import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { SearchInput } from 'app/design/primitives/input/search';
import { Input } from 'app/design/primitives/input';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { useCreateGroupConversation } from 'app/network/chat/channel';
import { useFindUsers, type FoundUser } from 'app/network/user';
import { MemberSearchResults } from 'app/screens/chat/components/member-search-results';
import { CommonActions } from '@react-navigation/native';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Pressable, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCamera, IconPencil } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { SelectedMemberBubble } from './selected-member-bubble';
import { MemberRow } from './member-row';

const theme = createDSTheme();

const MIN_GROUP_MEMBERS = 2;

export const NewGroupChatScreenComponent: NavigationScreen<'new-group-chat'> = ({ navigation: _navigation }) => {
  const { t } = useTranslation('new-group-chat');
  const navigation = useNavigation<'new-group-chat'>({ headerTitle: t('title') });

  const [groupName, setGroupName] = useState('');
  const [rawSearch, setRawSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<FoundUser[]>([]);
  const [groupPicture, setGroupPicture] = useState<ImagePickerAsset | undefined>(undefined);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { onUseImagePickerCamera } = useImagePickerCamera(
    (result) => {
      setGroupPicture(result.assets[0]);
    },
    undefined,
    { selectionLimit: 1 },
  );

  const canSearch = rawSearch.trim().length >= 3;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isSearching,
  } = useFindUsers(
    { search: rawSearch.trim(), groupIds: ['nearby'], sortBy: 'relevance', canReceiveInAppContact: true },
    { enabled: canSearch },
  );

  const results = canSearch ? (data?.pages.flatMap((p) => p.results) ?? []) : [];

  const selectedIds = new Set(selectedUsers.map((u) => u.id));
  const hasTooFewMembers = selectedUsers.length > 0 && selectedUsers.length < MIN_GROUP_MEMBERS;
  const showMinimumMembersError = hasAttemptedSubmit && hasTooFewMembers;

  const handleToggle = (member: FoundUser) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === member.id) ? prev.filter((u) => u.id !== member.id) : [...prev, member],
    );
  };

  const handleRemove = (member: FoundUser) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== member.id));
  };

  const { mutate: createGroup, isPending: isCreating } = useCreateGroupConversation({
    onSuccess: (channel) => {
      navigation.dispatch(
        CommonActions.reset({
          index: 1,
          routes: [{ name: 'all-channels' }, { name: 'one-channel', params: { url: channel.channel_url } }],
        }),
      );
    },
  });

  const handleNext = () => {
    if (selectedUsers.length === 0 || isCreating) return;
    if (selectedUsers.length < MIN_GROUP_MEMBERS) {
      setHasAttemptedSubmit(true);
      return;
    }

    createGroup({
      name: groupName.trim() || t('defaultGroupName', { count: selectedUsers.length }),
      userIds: selectedUsers.map((u) => u.id),
      picture: groupPicture,
    });
  };

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const renderItem = ({ item }: { item: FoundUser }) => (
    <MemberRow user={item} selected={selectedIds.has(item.id)} onToggle={handleToggle} />
  );

  const iconSize = 36;

  return (
    <Column flex={1} backgroundColor="surface.default">
      <Column paddingHorizontal="spacing.general.x8" paddingTop="spacing.general.x4" gap="spacing.general.x2">
        <Column alignItems="center" paddingVertical="spacing.general.x2">
          {!groupPicture ? (
            <Pressable
              onPress={() => onUseImagePickerCamera()}
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('choosePhoto')}
            >
              <Column
                alignItems="center"
                backgroundColor="surface.tertiary-neutral"
                borderColor="surface.disabled"
                borderRadius="radius.lg"
                borderStyle="dashed"
                borderWidth={1.5}
                height={120}
                justifyContent="center"
                paddingHorizontal="spacing.general.x4"
                width="100%"
              >
                <IconCamera strokeWidth={ICON_STROKE_WIDTH} size={iconSize} color={theme.colors['text.primary']} />
                <Text
                  color="text.primary"
                  fontWeight="600"
                  marginTop="spacing.general.x2"
                  textAlign="center"
                  variant="bodyBase"
                >
                  {t('choosePhoto')}
                </Text>
                <Text
                  color="text.tertiary"
                  fontSize={12}
                  marginTop="spacing.general.x0,5"
                  textAlign="center"
                  variant="bodyBase"
                >
                  {t('choosePhotoHint', { max: 1 })}
                </Text>
              </Column>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => onUseImagePickerCamera()}
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('choosePhoto')}
              style={{ position: 'relative' }}
            >
              <PCImage
                aspectRatio={1}
                cachePolicy="memory-disk"
                height={130}
                recyclingKey={`${groupPicture.uri}-group-header`}
                source={{ uri: groupPicture.uri }}
                style={{ borderRadius: theme.borderRadii['radius.lg'] }}
              />
              <View
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: theme.colors['text.primary'],
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={14} color="white" />
              </View>
            </Pressable>
          )}
        </Column>

        <Input onChangeText={setGroupName} placeholder={t('groupNamePlaceholder')} value={groupName} />

        <Text fontWeight="700" marginTop="spacing.general.x2">
          {t('addMembers', { count: selectedUsers.length })}
        </Text>

        {selectedUsers.length > 0 ? (
          <FlatList
            data={selectedUsers}
            horizontal
            keyExtractor={(user) => user.id}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item: user }) => <SelectedMemberBubble user={user} onRemove={handleRemove} />}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              gap: theme.spacing['spacing.general.x2'],
              paddingVertical: theme.spacing['spacing.general.x2'],
            }}
          />
        ) : null}

        {showMinimumMembersError ? (
          <Text accessibilityRole="alert" color="surface.error.default" variant="bodyBase">
            {t('minimumMembersError')}
          </Text>
        ) : null}

        <SearchInput onSubmit={setRawSearch} submitOnEmptyChange />
      </Column>

      <MemberSearchResults
        canSearch={canSearch}
        isSearching={isSearching || (results.length === 0 && isFetchingNextPage)}
        noResultsText={t('noResults')}
        onEndReached={handleEndReached}
        renderItem={renderItem}
        results={results}
      />

      <Column paddingHorizontal="spacing.general.x8" paddingVertical="spacing.general.x4">
        <Button
          disabled={selectedUsers.length === 0 || isCreating}
          loading={isCreating}
          onPress={handleNext}
          variant="primary"
        >
          {t('next')}
        </Button>
      </Column>
    </Column>
  );
};
